import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Palette, Check, Sun, Moon, Zap, Paintbrush, Rows } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './dialog';
import { usePalettes } from '@/hooks/usePalettes';
import type { ColorPaletteWithComputedFields } from '@/store/colorPalettes';
import { 
  COLOR_SECTIONS, 
  getDisplayColor, 
} from '../../lib/themeAwareColors';
import type { ChartType, ChartColor } from '@shared/schemas/chart';
import { getChartColorMode, type ChartColorMode } from '@/lib/chartColorUtils';

interface ChartColorPaletteChangerProps {
  className?: string;
  chartType?: ChartType;
  currentColor?: ChartColor;
  currentPaletteId?: string;
  onColorChange: (color: ChartColor) => void;
}

const ChartColorPaletteChanger: React.FC<ChartColorPaletteChangerProps> = ({
  className = '',
  chartType,
  currentColor,
  currentPaletteId,
  onColorChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { allPalettes, isLoading: palettesLoading, error: palettesError } = usePalettes();

  useEffect(() => {
    if (allPalettes && allPalettes.length > 0) {
      const sorted = [...allPalettes].sort((a, b) => {
        if (a.ownerId !== 'system' && b.ownerId === 'system') return -1;
        if (a.ownerId === 'system' && b.ownerId !== 'system') return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      // console.log('[ChartColorPaletteChanger Effect] Sorted palettes:', 
      //   sorted.map(p => ({ id: p.id, name: p.name, ownerId: p.ownerId, defaultColor: p.computedDefaultColor }))
      // );
    } else if (allPalettes) {
      // console.log('[ChartColorPaletteChanger Effect] allPalettes is empty or not yet populated.');
    }
  }, [allPalettes]);

  const chartColorMode = getChartColorMode(chartType);
  const showIndividualColorsSection = chartColorMode.usesSingleColor && !(chartColorMode.requiresPalette && !chartColorMode.usesSingleColor); // Show if it uses single, unless it ONLY requires palette
  const showPaletteSectionOnly = chartColorMode.requiresPalette && !chartColorMode.usesSingleColor;

  const isSingleColorContext = chartType && ['bar', 'line', 'area', 'scatter'].includes(chartType);
  
  const handleSingleColorSelect = (color: ChartColor) => {
    onColorChange(color);
    setIsOpen(false);
  };

  const handlePaletteCardSelect = (palette: ColorPaletteWithComputedFields) => {
    // Always use the palette's default color when the card is clicked
    onColorChange(palette.computedDefaultColor);
    setIsOpen(false);
  };

  const displayColorForIcon = currentColor 
    ? getDisplayColor(currentColor, 'light')
    : '#ef4444';

  const isCurrentColorThemeAwareForIcon = currentColor && typeof currentColor === 'object';

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative h-8 w-8 p-0 ${className}`}
          title={isSingleColorContext 
            ? `Change chart color${isCurrentColorThemeAwareForIcon ? ' (theme-aware)' : ''}`
            : `Change chart colors` 
          }
        >
          <Palette className="h-4 w-4" />
          <div className="absolute -bottom-0.5 -right-0.5">
            {isCurrentColorThemeAwareForIcon && typeof currentColor === 'object' ? (
              <div className="flex">
                <div
                  className="w-1 h-2 border border-background border-r-0"
                  style={{ backgroundColor: getDisplayColor(currentColor, 'light') }}
                />
                <div
                  className="w-1 h-2 border border-background border-l-0"
                  style={{ backgroundColor: getDisplayColor(currentColor, 'dark') }}
                />
              </div>
            ) : (
              <div
                className="w-2 h-2 rounded-full border border-background"
                style={{ backgroundColor: displayColorForIcon }}
              />
            )}
          </div>
        </Button>
      </DialogTrigger>
      
      <DialogContent className={`sm:max-w-[1024px] w-full max-h-[85vh] overflow-y-auto p-0 ${showPaletteSectionOnly ? 'sm:max-w-md' : 'sm:max-w-[1024px]'}`}>
        <DialogHeader className="px-6 pt-6 pb-4 sticky top-0 bg-background z-20 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            {showPaletteSectionOnly ? 'Choose Chart Palette' : 'Choose Chart Color or Palette'}
          </DialogTitle>
          <DialogDescription>
            {showPaletteSectionOnly 
              ? 'Select a color palette to apply to the chart segments.'
              : 'Select an individual color or apply a color from a palette.'}
          </DialogDescription>
        </DialogHeader>

        <div className={`flex flex-col ${showPaletteSectionOnly ? '' : 'md:flex-row'} gap-0 p-6`}>
          {/* Section 1: Individual Colors - Conditionally render */}
          {showIndividualColorsSection && (
            <div className="md:w-1/2 flex flex-col space-y-4 md:pr-3">
              {currentColor && (
                <div className="mb-4">
                  <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    Current Selection
                  </h3>
                  <div className="p-3 border rounded-lg bg-accent/30">
                    <div className="flex items-center gap-2">
                      {isCurrentColorThemeAwareForIcon && typeof currentColor === 'object' ? (
                        <>
                          <div className="flex">
                            <div
                              className="h-8 w-4 rounded-l border border-border/20 border-r-0"
                              style={{ backgroundColor: getDisplayColor(currentColor, 'light') }}
                              title={`Light: ${getDisplayColor(currentColor, 'light')}`}
                            />
                            <div
                              className="h-8 w-4 rounded-r border border-border/20 border-l-0"
                              style={{ backgroundColor: getDisplayColor(currentColor, 'dark') }}
                              title={`Dark: ${getDisplayColor(currentColor, 'dark')}`}
                            />
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <span className="font-mono">{getDisplayColor(currentColor, 'light')}</span>
                            <span className="mx-1">/</span>
                            <span className="font-mono">{getDisplayColor(currentColor, 'dark')}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className="h-8 w-8 rounded border border-border/20"
                            style={{ backgroundColor: displayColorForIcon }}
                            title={displayColorForIcon}
                          />
                          <span className="text-sm text-muted-foreground font-mono">{displayColorForIcon}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2 sticky top-[calc(theme(spacing.16)+1px)] bg-background py-2 z-10">
                  <Paintbrush className="h-5 w-5 text-primary" />
                  Individual Colors
                </h3>
                <div className="space-y-4 flex-grow">
                  {COLOR_SECTIONS.map((section) => (
                    <div key={section.id} className="space-y-3">
                      <div className="flex items-center gap-2">
                        {section.id === 'dark' && <Moon className="h-4 w-4" />}
                        {section.id === 'light' && <Sun className="h-4 w-4" />}
                        {section.id === 'universal' && <Zap className="h-4 w-4" />}
                        <h4 className="font-medium text-sm">{section.title}</h4>
                        <span className="text-xs text-muted-foreground">({section.description})</span>
                      </div>
                      
                      <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                        {section.colors.map((color, index) => {
                          const isThemeAwareSwatch = typeof color === 'object';
                          let isSelected = false;
                          if (isThemeAwareSwatch && typeof currentColor === 'object') {
                            isSelected = color.light === currentColor.light && color.dark === currentColor.dark;
                          } else if (!isThemeAwareSwatch && typeof currentColor === 'string') {
                            isSelected = color === currentColor;
                          }
                          
                          return (
                            <div
                              key={`${section.id}-${index}`}
                              className={`relative cursor-pointer rounded border-2 transition-all hover:scale-110 ${
                                isSelected 
                                  ? 'border-primary ring-2 ring-primary/20' 
                                  : 'border-border hover:border-primary/50'
                              }`}
                              onClick={() => handleSingleColorSelect(color)}
                              title={isThemeAwareSwatch 
                                ? `${color.light} / ${color.dark}` 
                                : color
                              }
                            >
                              {isThemeAwareSwatch ? (
                                <div className="flex h-8">
                                  <div
                                    className="flex-1 rounded-l"
                                    style={{ backgroundColor: color.light }}
                                  />
                                  <div
                                    className="flex-1 rounded-r"
                                    style={{ backgroundColor: color.dark }}
                                  />
                                </div>
                              ) : (
                                <div
                                  className="h-8 rounded"
                                  style={{ backgroundColor: color as string }}
                                />
                              )}
                              {isSelected && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Check className="h-4 w-4 text-white drop-shadow-lg" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Divider - Only show if both sections are visible */}
          {showIndividualColorsSection && !showPaletteSectionOnly && (
             <div className="hidden md:block border-l mx-3"></div>
          )}

          {/* Section 2: Choose from Palettes */}
          <div className={`${showIndividualColorsSection ? 'md:w-1/2' : 'w-full'} flex flex-col space-y-4 ${showIndividualColorsSection ? 'md:pl-3' : ''}`}>
            <h3 className="text-lg font-semibold flex items-center gap-2 sticky top-[calc(theme(spacing.16)+1px)] bg-background py-2 z-10">
              <Rows className="h-5 w-5 text-primary" />
              {showPaletteSectionOnly ? 'Available Palettes' : 'Choose from Palettes'}
            </h3>
            {palettesLoading && <p className="text-sm text-muted-foreground">Loading palettes...</p>}
            {palettesError && <p className="text-sm text-destructive">Error loading palettes: {palettesError}</p>}
            {!palettesLoading && !palettesError && (
              <div className={`grid grid-cols-1 ${chartColorMode.requiresPalette ? 'sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'} gap-3 flex-grow`}>
                {[...allPalettes]
                  .sort((a, b) => {
                    if (a.ownerId !== 'system' && b.ownerId === 'system') return -1;
                    if (a.ownerId === 'system' && b.ownerId !== 'system') return 1;
                    return (a.name || '').localeCompare(b.name || '');
                  })
                  .map((palette: ColorPaletteWithComputedFields) => {
                    const isFullPaletteSelectedForThisCard = chartColorMode.requiresPalette && currentPaletteId === palette.id;
                    const cardTitle = `Apply Default Color from ${palette.name}`;

                    return (
                      <div
                        key={palette.id}
                        className={`relative rounded-lg border-2 p-3 space-y-2 transition-all hover:shadow-md ${chartColorMode.usesSingleColor || chartColorMode.requiresPalette ? 'cursor-pointer' : 'cursor-default'} ${
                          isFullPaletteSelectedForThisCard 
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/30' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => handlePaletteCardSelect(palette)} 
                        title={cardTitle}
                      >
                        <h4 className="font-medium text-sm truncate" title={palette.name}>{palette.name}</h4>
                        <div className={`grid ${palette.colors.length > 5 ? 'grid-cols-5' : 'grid-cols-'+Math.max(1, palette.colors.length)} gap-1.5`}>
                          {palette.colors.map((color, index) => {
                            // A swatch is selected if it IS the currentColor AND no full palette is active.
                            // If a full palette is active, no individual swatches get primary selection tick.
                            const isThisSwatchTheCurrentSingleColor = 
                              !currentPaletteId && // No full palette is selected
                              currentColor === color;
                            return (
                              <div
                                key={`${palette.id}-${index}`}
                                className={`relative rounded aspect-square border transition-all ${
                                  chartColorMode.usesSingleColor ? 'cursor-pointer hover:scale-110' : 'cursor-default' // Swatch clickable only if chart uses single colors
                                } ${
                                  isThisSwatchTheCurrentSingleColor 
                                    ? 'border-primary ring-2 ring-primary/20' // Selected single swatch style
                                    : 'border-border/30'
                                }`}
                                style={{ backgroundColor: color }}
                                onClick={(e) => {
                                  // Only allow swatch click to set single color if the chart type supports single colors
                                  if (chartColorMode.usesSingleColor) {
                                    e.stopPropagation(); 
                                    handleSingleColorSelect(color);
                                  } else {
                                    // If chart only requires full palettes, swatch click might do nothing or also apply full palette (like card)
                                    // For now, let it do nothing if not chartColorMode.usesSingleColor
                                    e.stopPropagation();
                                  }
                                }}
                                title={color}
                              >
                                {isThisSwatchTheCurrentSingleColor && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <Check className="h-3 w-3 text-white drop-shadow-md" />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChartColorPaletteChanger; 